<template>
  <div
    v-show="paxSelector && paxSelector.dropdown.open"
    class="pax-modal"
    tabindex="-1"
  >
    <modal-header-white
      v-if="paxSelector && paxSelector.dropdown.open"
      :device-type="deviceType"
      class="pax-modal-header"
      :label="$t('searchForm.paxSelector.modalWindow.headerTextEHorizon')"
      @closed="onClosed"
    />
    <div class="pax-modal-body">
      <pax-input
        ref="adults"
        :title="$t(`searchForm.paxSelector.adults.titleEHorizon`, { count: paxSelector.adults }, paxSelector.adults)"
        :subtitle="$t(adultSubtitle)"
        :value="paxSelector.adults"
        :min-value="paxSelector.config.minAdults"
        :max-value="paxSelector.config.maxAdults"
        :parent-count="paxSelector.config.maxAdults"
        :error-message="groupErrorMessage"
        @setValue="setAdults"
      />
      <pax-input
        v-if="isYoungAdultsAvailable"
        ref="youngAdults"
        :title="$t(`searchForm.paxSelector.youngAdults.titleEHorizon`, { count: paxSelector.youngAdults }, paxSelector.youngAdults)"
        :subtitle="$t(`searchForm.paxSelector.youngAdults.subtitleEHorizon`)"
        :value="paxSelector.youngAdults"
        :min-value="paxSelector.config.minYoungAdults"
        :max-value="paxSelector.config.maxYoungAdults"
        :parent-count="paxSelector.config.maxYoungAdults"
        @setValue="setYoungAdults"
      />
      <pax-input
        ref="children"
        :title="$t('searchForm.paxSelector.children.titleEHorizon', { count: paxSelector.children }, paxSelector.children)"
        :subtitle="$t('searchForm.paxSelector.children.subtitleEHorizon')"
        :value="paxSelector.children"
        :min-value="paxSelector.config.minChildren"
        :max-value="paxSelector.config.maxChildren"
        :parent-count="paxSelector.config.maxChildren"
        @setValue="setChildren"
      />
      <pax-input
        ref="infants"
        :title="$t('searchForm.paxSelector.infants.titleEHorizon', {count: paxSelector.infants}, paxSelector.infants)"
        :subtitle="$t('searchForm.paxSelector.infants.subtitleEHorizon')"
        :value="paxSelector.infants"
        :min-value="paxSelector.config.minInfants"
        :max-value="paxSelector.config.maxInfants"
        :parent-count="paxSelector.adults"
        :error-message="infantsErrorMessage"
        @setValue="setInfants"
      />
    </div>
    <div class="pax-modal-footer">
      <button
        tabindex="-1"
        type="button"
        @click.prevent="onClosed"
      >
        {{ $t('searchForm.paxSelector.modalWindow.footerTextEHorizon') }}
      </button>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import PaxInput from 'components/eHorizon/PaxSelector/PaxInput'
  import ModalHeaderWhite from 'components/Modal/ModalHeaderWhite'

  const events = {
    CLICKED: 'clicked',
    SET_ADULTS: 'setAdults',
    SET_YOUNG_ADULTS: 'setYoungAdults',
    SET_CHILDREN: 'setChildren',
    SET_INFANTS: 'setInfants'
  }

  export default {
    components: {
      PaxInput,
      ModalHeaderWhite
    },
    props: {
      paxSelector: {
        type: Object,
        required: true
      },
      isYoungAdultsAvailable: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      ...mapState('device', [
        'deviceType'
      ]),
      infantsErrorMessage () {
        return this.paxSelector.infantCountExceeded ? this.$t('searchForm.paxSelector.error.infantsMoreThanAdultsEHorizon') : ''
      },
      groupErrorMessage () {
        return this.paxSelector.groupBooking ? this.$t('searchForm.paxSelector.error.groupBookingEHorizon') : ''
      },
      adultSubtitle() {
        return this.isYoungAdultsAvailable
          ? 'searchForm.paxSelector.adultsYTH.subtitleEHorizon'
          : 'searchForm.paxSelector.adults.subtitleEHorizon'
      }
    },
    methods: {
      onClosed () {
        if (this.paxSelector.dropdown.open) {
          this.$emit(events.CLICKED)
        }
      },
      setAdults (value) {
        this.$emit(events.SET_ADULTS, value)
      },
      setYoungAdults(value) {
        this.$emit(events.SET_YOUNG_ADULTS, value)
      },
      setChildren (value) {
        this.$emit(events.SET_CHILDREN, value)
      },
      setInfants (value) {
        this.$emit(events.SET_INFANTS, value)
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .pax-modal {
    display: flex;
    flex-direction: column;
    justify-content: center;
    z-index: 100;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background-color: #ffffff;

    @include bp-mf-tablet {
      position: fixed;
      z-index: 2;
      padding: 0;
      background-color: #ffffff;
      border-radius: 6px;
      width: 320px;
      min-height: 485px;
      max-height: 560px;
      height: auto;
      left: calc(50% - 160px);
      top: calc(50% - 240px);
    }

    @include  bp-mf-desktop {
      position: absolute;
      width: 260px;
      min-height: 200px;
      top: -8px;
      left: -8px;
      border-radius: 3px;
      box-shadow: inset -1px 0 0 0 hexToRGBA(#000000, 0.1), 0 2px 10px 0 hexToRGBA(#000000, 0.16);
    }

    &.open {
      display: block;
    }

    .pax-modal-header {
      background-color: #ffffff;

      @include bp-mf-tablet {
        display: block;
        height: 56px;
        padding: 28px 24px;
      }

      @include bp-mf-desktop {
        display: none;
      }

      ::v-deep .modal-title {
        color: $blue-dark;
        font-size: em(14);
        font-weight: 500;
        line-height: 16px;
      }

      ::v-deep .close-icon {
        font-size: em(14);
        right: 25px;
      }
    }

    .pax-modal-body {
      margin: 95px 24px 0;
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: auto;

      @include  bp-mf-tablet {
        margin: 40px 0 0;
        padding: 0 24px;
      }

      @include bp-mf-desktop {
        margin: 0;
        padding: 16px 16px 0 16px;
      }
    }

    .pax-modal-footer {
      margin: 25px 25px 50px;

      @include bp-mf-tablet {
        margin-bottom: 56px;
        margin-top: 0;
      }

      @include bp-mf-desktop {
        margin-bottom: 0;
        margin-left: 16px;
        margin-right: 16px;
      }

      button {
        border-radius: 4px;
        background-color: $blue-medium;
        box-shadow: inset 1px 0 0 0 hexToRGBA(#000000, 0.1);
        color: #ffffff;
        font-size: em(14);
        font-weight: 500;
        line-height: 16px;
        text-align: center;
        padding: 16px 24px;

        &:hover {
          color: #ffffff;
          background: $blue-light;
        }

        @include bp-mf-desktop {
          border-radius: 0;
          background-color: #ffffff;
          box-shadow: none;
          margin: 0;
          color: $blue-medium;
          padding: 0 0 24px;
          text-align: right;
          font-size: em(16);
          font-weight: 500;
          line-height: 19px;

          &:hover {
            color: $blue-light;
            background: #ffffff;
          }
        }
      }
    }
  }

</style>
